import { Icon } from '../icon';
import { ActiveJob } from '@/hooks/useJobProgress';

interface ActiveUploadsProgressProps {
  activeJobs: ActiveJob[];
  zipIsProcessing: boolean;
}

export function ActiveUploadsProgress({ activeJobs, zipIsProcessing }: ActiveUploadsProgressProps) {
  if (activeJobs.length === 0 && zipIsProcessing) {
    return (
      <div className="mt-3 space-y-2">
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
          <div className="flex items-center gap-2">
            <Icon className="lucide--loader2 h-3.5 w-3.5 animate-spin text-blue-500" />
            <span className="text-sm font-medium">Waiting for Zip to begin processing...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3 space-y-2">
      {activeJobs.map((job) => {
        const progressPercentage = job.totalFiles > 0 ? Math.round((job.processedFiles / job.totalFiles) * 100) : 0;

        const getJobIcon = () => {
          switch (job.type) {
            case 'unzip':
              return <Icon className="lucide--file-archive text-brandSecondary h-3.5 w-3.5" />;
            case 'relativity':
              return <Icon className="lucide--download text-brandSecondary h-3.5 w-3.5" />;
            default:
              return <Icon className="lucide--file-text text-brandSecondary h-3.5 w-3.5" />;
          }
        };

        const getJobTypeLabel = () => {
          switch (job.type) {
            case 'unzip':
              return 'Extracting Zip';
            case 'relativity':
              return 'Relativity Import';
            default:
              return 'Processing';
          }
        };

        // Determine the display text and progress based on job state
        const isPreparing = job.progressStatus === 'preparing';
        const displayProgress = isPreparing ? (job.preparingProgress || 0) : progressPercentage;
        const displayText = isPreparing
          ? (job.preparingMessage || `Preparing... ${job.preparingProgress || 0}%`)
          : job.totalFiles === 0
            ? 'Preparing...'
            : `${job.processedFiles.toLocaleString()} / ${job.totalFiles.toLocaleString()} files (${progressPercentage}%)`;

        return (
          <div key={job.id} className="rounded-lg border border-gray-200 bg-white p-3">
            <>
              <div className="mb-2 flex items-center justify-between">
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  {getJobIcon()}
                  <span className="truncate text-sm font-medium text-gray-900">{getJobTypeLabel()}</span>
                  <span className="truncate text-xs text-gray-500">{job.fileName}</span>
                </div>

                <span className="ml-2 flex-shrink-0 text-xs font-medium text-gray-900">
                  {displayText}
                </span>
              </div>

              {/* Compact Progress Bar */}
              <div className="h-1.5 w-full rounded-full bg-gray-200">
                <div
                  className="bg-brandSecondary h-1.5 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${displayProgress}%` }}
                />
              </div>
            </>
          </div>
        );
      })}
    </div>
  );
}
